import React, { useEffect, useState } from "react";
import { TodoList, TodoListShare } from "@/api/types";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import * as Dialog from "@radix-ui/react-dialog";
import { uiState } from "@/states.ts";
import { apiCreateExternalShare, apiCreateShare, apiDeleteShare, fetchShares } from "@/api/api.ts";
import {
	LucideCopy,
	LucideExternalLink,
	LucideEye,
	LucidePencil,
	LucideTrash2,
} from "lucide-react";

export interface ShareListDialogProps {
	list: TodoList;
}

const ShareListDialog: React.FC<ShareListDialogProps> = (props) => {
	const [email, setEmail] = useState("");
	const [shares, setShares] = useState<TodoListShare[]>([]);
	const [loading, setLoading] = useState(false);
	const [internalPermission, setInternalPermission] = useState<"read" | "write">("write");
	const [externalPermission, setExternalPermission] = useState<"read" | "write">("read");

	useEffect(() => {
		fetchShares(props.list.id).then((res) => {
			if (res && res.ok) {
				setShares(res.shares);
			}
		});
	}, [props.list.id]);

	const handleAddShare = async () => {
		if (!email) return;
		setLoading(true);
		try {
			const res = await apiCreateShare(props.list.id, email, internalPermission);
			if (res?.ok) {
				setShares([...shares, res.share]);
				setEmail("");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleAddExternalShare = async () => {
		setLoading(true);
		try {
			const res = await apiCreateExternalShare(props.list.id, externalPermission);
			if (res?.ok) {
				setShares([...shares, res.share]);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteShare = async (shareId: string) => {
		const res = await apiDeleteShare(props.list.id, shareId);
		if (res?.ok) {
			setShares(shares.filter((s) => s.id !== shareId));
		}
	};

	const handleClose = () => {
		uiState.sharingList = null;
	};

	const copyLink = (token: string) => {
		const url = `${window.location.origin}/lists/${props.list.id}/items?share_token=${token}`;
		navigator.clipboard.writeText(url);
		alert("Link copied to clipboard");
	};

	return (
		<Dialog.Root open={true}>
			<Dialog.Portal>
				<Dialog.Overlay className="DialogOverlay" />
				<Dialog.Content className="DialogContent">
					<div className={"p-4 flex flex-col space-y-4"}>
						<Dialog.Title className="DialogTitle text-xl font-bold">
							Share "{props.list.title}"
						</Dialog.Title>

						<div className="space-y-2">
							<p className="text-sm font-medium">
								Share with user (email)
							</p>
							<div className={"flex gap-2"}>
								<Input
									placeholder={
										"user@example.com"
									}
									value={email}
									className={
										"bg-secondary/30"
									}
									onChange={(e) =>
										setEmail(
											e.target
												.value,
										)
									}
								/>
								<div className="flex border rounded overflow-hidden">
									<Button
										variant={
											internalPermission ===
											"read"
												? "secondary"
												: "ghost"
										}
										size="icon"
										className="h-10 w-10 rounded-none"
										onClick={() =>
											setInternalPermission(
												"read",
											)
										}
										title="Read only"
									>
										<LucideEye
											size={16}
										/>
									</Button>
									<Button
										variant={
											internalPermission ===
											"write"
												? "secondary"
												: "ghost"
										}
										size="icon"
										className="h-10 w-10 rounded-none"
										onClick={() =>
											setInternalPermission(
												"write",
											)
										}
										title="Full access"
									>
										<LucidePencil
											size={16}
										/>
									</Button>
								</div>
								<Button
									onClick={handleAddShare}
									disabled={loading || !email}
								>
									Add
								</Button>
							</div>
						</div>

						<div className="space-y-2">
							<p className="text-sm font-medium">
								External Link
							</p>
							<div className="flex gap-2">
								<div className="flex border rounded overflow-hidden flex-1">
									<Button
										variant={
											externalPermission ===
											"read"
												? "secondary"
												: "ghost"
										}
										className="flex-1 rounded-none h-10"
										onClick={() =>
											setExternalPermission(
												"read",
											)
										}
									>
										<LucideEye
											size={16}
											className="mr-2"
										/>
										Read Only
									</Button>
									<Button
										variant={
											externalPermission ===
											"write"
												? "secondary"
												: "ghost"
										}
										className="flex-1 rounded-none h-10"
										onClick={() =>
											setExternalPermission(
												"write",
											)
										}
									>
										<LucidePencil
											size={16}
											className="mr-2"
										/>
										Full Access
									</Button>
								</div>
								<Button
									variant="outline"
									onClick={
										handleAddExternalShare
									}
									disabled={loading}
									className="px-4"
								>
									<LucideExternalLink className="mr-2 h-4 w-4" />
									Generate
								</Button>
							</div>
						</div>

						<div className="space-y-2 pt-2">
							<p className="text-sm font-medium">
								Shared with
							</p>
							<div className="max-h-40 overflow-y-auto space-y-2">
								{shares.length === 0 && (
									<p className="text-sm text-muted-foreground italic">
										Not shared with
										anyone yet.
									</p>
								)}
								{shares.map((s) => (
									<div
										key={s.id}
										className="flex items-center justify-between p-2 bg-secondary/20 rounded"
									>
										<div className="flex flex-col">
											<span className="text-sm font-medium">
												{s.email ||
													"External Link"}
											</span>
											<span className="text-xs text-muted-foreground">
												{
													s.permission
												}{" "}
												access
											</span>
										</div>
										<div className="flex gap-1">
											{s.external_token && (
												<Button
													variant="ghost"
													size="icon"
													onClick={() =>
														copyLink(
															s.external_token!,
														)
													}
												>
													<LucideCopy
														size={
															16
														}
													/>
												</Button>
											)}
											<Button
												variant="ghost"
												size="icon"
												className="text-destructive"
												onClick={() =>
													handleDeleteShare(
														s.id,
													)
												}
											>
												<LucideTrash2
													size={
														16
													}
												/>
											</Button>
										</div>
									</div>
								))}
							</div>
						</div>

						<div className={"flex justify-end pt-4"}>
							<Button
								variant={"ghost"}
								onClick={handleClose}
							>
								Close
							</Button>
						</div>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export { ShareListDialog };
